import { useEffect, useRef, useState } from 'react';
import { api, type BannerView } from './api.js';
import type { Strings } from './i18n.js';

/**
 * The advertising slot on the payment page.
 *
 * Three rules govern this component, and every one of them is a constraint
 * rather than a preference.
 *
 * 1. Nothing here comes from anywhere but this platform. No third-party script,
 *    no external iframe, no remote image, no tracking pixel, no remote font.
 *    The page's Content-Security-Policy is `default-src 'none'` with Stripe
 *    added back, and that policy is what keeps the platform inside PCI DSS
 *    v4.0.1 SAQ A. The banner is an <img> holding bytes we served and an <a>
 *    that opens in a new tab, which is all a self-hosted advertisement needs to
 *    be. See compliance/payment-page-advertising.md.
 *
 * 2. It must not be mistakable for part of the payment. It sits outside the
 *    <form>, below it, behind a rule, on the page background rather than on a
 *    panel, and it is labelled as advertising in the customer's own language.
 *    A payer must never be in doubt about which part of this page is the thing
 *    they are paying for.
 *
 * 3. It must not be able to interrupt a payment. The anchor is `target="_blank"`
 *    with `rel="noopener noreferrer nofollow"`, so a click opens elsewhere and
 *    the payment page - with a half-completed Payment Element in it - is left
 *    alone. Nothing in this file navigates, redirects or moves the page, and
 *    the element is in normal flow with no positioning and no z-index, so it
 *    cannot overlay or obscure the Stripe element above it.
 *
 * And one rule about how many: exactly one advertisement per page view, or
 * none. The server returns a single banner rather than a list, this component
 * asks for it once, and there is nothing here that renders a second.
 */
export function Advertisement({ token, t }: { token: string; t: Strings }) {
  const [banner, setBanner] = useState<BannerView['banner']>(null);

  /**
   * An impression is counted once, when the image has actually rendered.
   *
   * Not when the banner is fetched: a slot that was requested but never painted
   * is not something an advertiser should be invoiced for. The ref survives the
   * double effect invocation React's StrictMode performs in development, which
   * would otherwise count every display twice.
   */
  const counted = useRef<string | null>(null);

  /**
   * One request per page view, guarded rather than assumed.
   *
   * Each call advances the rotation on the server, so a component that asks
   * twice does not merely waste a request - it takes two positions in the
   * rotation for one page view, shows the second banner, and can count an
   * impression against the first. StrictMode invokes effects twice in
   * development, and a future edit adding a dependency to the array below would
   * do the same in production. The ref makes "once" true in both cases.
   */
  const asked = useRef(false);

  useEffect(() => {
    if (asked.current) return;
    asked.current = true;

    let cancelled = false;
    api
      .banner(token)
      .then((view) => {
        if (!cancelled) setBanner(view.banner);
      })
      .catch(() => {
        // An advertisement that cannot load is not an error worth telling a
        // customer about, and certainly not one worth interrupting a payment
        // for. The slot simply stays empty.
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (!banner) return null;

  /**
   * Defence in depth on the destination.
   *
   * The server refuses anything that is not https on the way in, so this should
   * never fire. It is here because the failure it guards against - a
   * `javascript:` URL reaching an href on the page where a card number is typed
   * - is severe enough that one validation is not enough of them. A banner with
   * an unusable destination is shown as a plain image rather than not at all.
   */
  const linkable = banner.destination_url.startsWith('https://');

  /**
   * The box, in CSS pixels, taken from the named format the creative was
   * accepted as.
   *
   * Uploads are now confined to three sizes - 728x90, 468x60 and 320x50, each
   * also accepted at exactly twice those dimensions and displayed at the base
   * size - so every creative in a slot occupies the same space and the slot
   * stops changing shape between one advertisement and the next. The aspect
   * ratio is declared on the wrapper so the height is reserved before the bytes
   * are decoded, which is what keeps the page from moving under somebody who is
   * already reading it.
   *
   * A null slot is a creative uploaded before any of this existed. Those are
   * bounded rather than reserved for, which is what this component did for
   * every banner until now.
   */
  const box: React.CSSProperties =
    banner.display_width && banner.display_height
      ? { maxWidth: banner.display_width, aspectRatio: `${banner.display_width} / ${banner.display_height}` }
      : { maxWidth: 728 };

  const image = (
    <img
      src={banner.image_data_uri}
      alt={banner.alt_text}
      onLoad={() => {
        if (counted.current === banner.id) return;
        counted.current = banner.id;
        void api.bannerEvent(token, banner.id, 'impression').catch(() => undefined);
      }}
      style={{
        display: 'block',
        width: '100%',
        height: banner.display_height ? '100%' : 'auto',
        maxHeight: banner.display_height ? undefined : 160,
        objectFit: 'contain',
      }}
    />
  );

  return (
    <aside
      aria-label={t.advertisement}
      style={{
        // Normal flow, no position, no z-index: this element has no way to sit
        // over the payment form even if a future stylesheet tried to give it
        // one from here.
        marginTop: 32,
        paddingTop: 18,
        borderTop: '1px solid var(--wc-border)',
      }}
    >
      <p
        style={{
          margin: '0 0 8px',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--wc-faint)',
        }}
      >
        {t.advertisement}
      </p>

      <div style={{ ...box, border: '1px solid var(--wc-border)', background: 'var(--wc-surface)' }}>
        {linkable ? (
          <a
            href={banner.destination_url}
            // Opens away from the payment. `noopener` denies the destination a
            // handle on this window, `noreferrer` stops the payment URL - which
            // carries the payment link token - being sent as a referrer, and
            // `nofollow` says the platform is not endorsing what it rents space
            // to.
            target="_blank"
            rel="noopener noreferrer nofollow"
            onClick={() => {
              void api.bannerEvent(token, banner.id, 'click').catch(() => undefined);
            }}
            // Full height as well as full width, so the image inside can fill
            // the box the slot reserved rather than sitting in the top of it.
            style={{ display: 'block', height: '100%' }}
          >
            {image}
          </a>
        ) : (
          image
        )}
      </div>

      <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--wc-faint)', lineHeight: 1.45 }}>
        {t.adNote}
      </p>
    </aside>
  );
}
